import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Loader2, Send, Lightbulb, CheckCircle } from 'lucide-react';
import { Label } from '../components/ui/label';
import api from '../services/api';

const IdeaPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      await api.post('/feedback/idea', {
        email: user.email,
        title: formData.title.trim(),
        description: formData.description.trim()
      });
      setSuccess(true);
      setFormData({ title: '', description: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send idea. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl" data-testid="idea-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-status-warning/10 rounded-sm flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-status-warning" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-cockpit-text-primary">Got an Idea?</h1>
            <p className="text-sm text-cockpit-text-secondary">Help us improve Flight Sim Academy</p>
          </div>
        </div>

        {/* Success State */}
        {success ? (
          <div className="card-panel p-8 text-center" data-testid="idea-success">
            <div className="w-16 h-16 bg-status-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-status-success" />
            </div>
            <h2 className="text-lg font-medium text-cockpit-text-primary mb-2">Idea Submitted</h2>
            <p className="text-cockpit-text-secondary mb-6">Thanks for your suggestion — we appreciate your feedback.</p>
            <button
              onClick={() => setSuccess(false)}
              className="btn-secondary"
              data-testid="idea-send-another"
            >
              Submit Another Idea
            </button>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-900/30 border border-red-900 rounded-sm">
                <p className="text-sm text-red-300" data-testid="idea-error">{error}</p>
              </div>
            )}

            <div className="card-panel p-6">
              <div className="space-y-4">
                {/* Email (Read-only) */}
                <div>
                  <Label className="label-text block mb-2">Your Email</Label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    readOnly
                    className="input-field w-full bg-cockpit-bg/50 cursor-not-allowed opacity-70"
                    data-testid="idea-email"
                  />
                  <p className="text-xs text-cockpit-text-secondary/50 mt-1">
                    We may reach out if we have questions about your idea
                  </p>
                </div>

                {/* Title */}
                <div>
                  <Label className="label-text block mb-2">Idea Title</Label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="input-field w-full"
                    placeholder="Give your idea a short title"
                    maxLength={100}
                    data-testid="idea-title"
                  />
                </div>

                {/* Description */}
                <div>
                  <Label className="label-text block mb-2">Description</Label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="input-field w-full min-h-[150px] resize-y"
                    placeholder="Describe your idea in detail. What problem does it solve? How would it work?"
                    maxLength={2000}
                    data-testid="idea-description"
                  />
                  <p className="text-xs text-cockpit-text-secondary/50 mt-1">
                    {formData.description.length}/2000 characters
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex items-center gap-2"
              data-testid="idea-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Idea
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
};

export default IdeaPage;
